import React, { ReactNode, createContext, useContext, useEffect } from 'react';
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from '@tanstack/react-query';
import { apiRequest, getQueryFn, queryClient } from '../lib/queryClient';
import { useToast } from '../hooks/use-toast';

type User = {
  id: number;
  email: string;
  full_name?: string;
  role: string;
  is_active: boolean;
  is_system_user: boolean;
  created_at: string;
};

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<any, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<any, Error, RegisterData>;
};

type LoginData = {
  email: string;
  password: string;
};

type RegisterData = {
  email: string;
  password: string;
  full_name?: string;
  role?: string;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();

  const {
    data: user,
    error,
    isLoading,
  } = useQuery<User | null, Error>({
    queryKey: ['/api/user'],
    queryFn: async ({ queryKey }) => {
      const token = localStorage.getItem('access_token');
      if (!token) return null;
      
      try {
        const res = await apiRequest('GET', '/api/user');
        if (!res.ok) {
          if (res.status === 401) return null;
          throw new Error(`HTTP ${res.status}`);
        }
        return await res.json();
      } catch (error) {
        return null;
      }
    },
    enabled: !!localStorage.getItem('access_token'),
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      try {
        const res = await apiRequest('POST', '/api/login', credentials);
        return await res.json();
      } catch (error: any) {
        throw new Error(error.message || 'Login failed');
      }
    },
    onSuccess: (data: { access_token: string; user: User }) => {
      localStorage.setItem('access_token', data.access_token);
      queryClient.setQueryData(['/api/user'], data.user);
      toast({
        title: 'Welcome back!',
        description: 'You have successfully logged in.',
      });
    },
    onError: (error: Error) => {
      console.error('Login error:', error);
      const unreachable = /failed to fetch|network error|load failed/i.test(error.message || '');
      toast({
        title: 'Login failed',
        description: unreachable
          ? 'We could not reach the server. Start the API, then try again.'
          : error.message || 'Invalid email or password',
        variant: 'destructive',
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (newUser: RegisterData) => {
      try {
        const res = await apiRequest('POST', '/api/register', newUser);
        return await res.json();
      } catch (error: any) {
        throw new Error(error.message || 'Registration failed');
      }
    },
    onSuccess: (data: { access_token: string; user: User }) => {
      localStorage.setItem('access_token', data.access_token);
      queryClient.setQueryData(['/api/user'], data.user);
      toast({
        title: 'Account created!',
        description: 'Your account has been created successfully.',
      });
    },
    onError: (error: Error) => {
      console.error('Registration error:', error);
      toast({
        title: 'Registration failed',
        description: error.message || 'Unable to create account',
        variant: 'destructive',
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Clear token regardless of server response
      localStorage.removeItem('access_token');
    },
    onSuccess: () => {
      queryClient.setQueryData(['/api/user'], null);
      queryClient.clear();
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out.',
      });
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
